import type { UserReferenceUser } from '@/components/UserReference/UserReference.types';
import { compareUsersByName } from '@/user/compareUsersByName';
import { useUserList } from '@/user/composables/useUserList';
import type { MappedUser } from '@/user/user';
import { computed } from 'vue';
import { useEventInjection } from './useEventInjection';

function mapToReferenceUser(user: MappedUser): UserReferenceUser {
    return {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        avatarUrl: user.avatarUrl,
        company: user.company,
        jobTitle: user.jobTitle,
        hasLoginMethod: Boolean(user.passwordLogin) || user.ssoLogins.length > 0,
    };
}

export function useEventResponsibleUsers() {
    const { event } = useEventInjection();
    const { users } = useUserList();

    const savedResponsibleUsers = computed(() => event.value?.eventResponsibleUsers.nodes.map((n) => n.user) ?? []);

    // The user list also contains not yet activated Entra users, whose ids don't belong to a user row
    const activeUsers = computed(() =>
        (users.value ?? []).filter((user) => user.activationStatus === 'Active').map(mapToReferenceUser),
    );

    const usersById = computed(() => {
        const result = new Map<string, UserReferenceUser>();
        for (const user of savedResponsibleUsers.value) {
            // Shown with what the event knows until the user list has loaded
            result.set(user.id, {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                hasLoginMethod: true,
            });
        }
        for (const user of users.value ?? []) {
            result.set(user.id, mapToReferenceUser(user));
        }
        return result;
    });

    const responsibleUsers = computed(() =>
        savedResponsibleUsers.value.flatMap((user) => usersById.value.get(user.id) ?? []).toSorted(compareUsersByName),
    );

    return {
        savedResponsibleUsers,
        activeUsers,
        usersById,
        responsibleUsers,
    };
}
